import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Checkbox,
  ListItemText,
  TableSortLabel,
  Typography,
  Card,
  InputAdornment,
  Snackbar,
  Alert,
  CardContent,
  Tabs,
  Tab,
  Skeleton,
} from '@mui/material';
import { 
  Add, 
  FilterList as FilterListIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Science as ScienceIcon,
  LocalHospital as HospitalIcon,
  PersonPin as PathologistIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Error as ErrorIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useOrders } from './hooks/useOrders';
import { useOrderFilters } from './hooks/useOrderFilters';
import { useOrderDialog } from './hooks/useOrderDialog';
import OrderTableRow from './OrderTableRow';

import OrderDialog from './OrderDialog';
import VersionHistoryDialog from './VersionHistoryDialog';
import apiService from '../../services/api';

interface Patient {
  id: string; // UUID from GraphQL
  patientId: string;
  name: string;
  dob: string;
  sex?: string;
  mrn?: string;
  locationId?: string;
  createdAt?: string;
}

interface User {
  id: number;
  name: string;
  role: string;
}

interface Location {
  location_id: number | string;
  labName: string;
}

interface UserWithRoles {
  id: number;
  name: string;
  email: string;
  roles?: { id: number; name: string; description?: string }[];
}

interface OrdersListProps {
  onRefresh?: () => void;
}

const OrdersList: React.FC<OrdersListProps> = React.memo(({
  onRefresh,
}) => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedStatusTab, setSelectedStatusTab] = useState(0);
  const [searchInputRef, setSearchInputRef] = useState<HTMLInputElement | null>(null);

  const {
    orders,
    page,
    rowsPerPage,
    total,
    loading: ordersLoading,
    pdfGenerating,
    pdfGeneratingOrderId,
    pdfSuccess,
    selectedStatus,
    statusCounts,
    fetchOrders,
    fetchStatusCounts,
    handleChangePage,
    handleChangeRowsPerPage,
    handleStatusChange,
    deleteOrder,
    generatePDF,
  } = useOrders();

  const {
    sortColumn,
    sortDirection,
    accessionFilterValue,
    setAccessionFilterValue,
    patientFilterValue,
    setPatientFilterValue,
    selectedLocationIds,
    setSelectedLocationIds,
    accessionFilterAnchorEl,
    setAccessionFilterAnchorEl,
    patientFilterAnchorEl,
    setPatientFilterAnchorEl,
    locationFilterAnchorEl,
    setLocationFilterAnchorEl,
    searchValue,
    setSearchValue,
    handleSort,
    clearAccessionFilter,
    clearPatientFilter,
    clearLocationFilter,
    sortedOrders,
    patientOrdersMap,
  } = useOrderFilters(orders, patients);

  const {
    openDialog,
    editingOrder,
    isAddendumMode,
    isAmendMode,
    versionHistoryOpen,
    versionHistory,
    versionHistoryLoading,
    handleEdit,
    handleDialogClose,
    handleSave,
    handleFinalize,
    handleAmend,
    handleAddendum,
    handleArchive,
    handleViewVersionHistory,
    isPathologist,
    setVersionHistoryOpen,
  } = useOrderDialog(() => {
    // Refresh orders after save - include current status filter
    fetchOrders(page, rowsPerPage, searchValue, selectedStatus);
    // Also refresh status counts
    fetchStatusCounts();
  });

  // Memoize the combined loading state to prevent unnecessary re-renders
  const isLoading = useMemo(() => {
    return loading || ordersLoading;
  }, [loading, ordersLoading]);

  // Memoize dropdown data to prevent unnecessary re-renders
  const dropdownData = useMemo(() => ({
    patients,
    users,
    locations
  }), [patients, users, locations]);

  const fetchDropdowns = useCallback(async () => {
    setLoading(true);
    try {
      const [patientsData, usersData, locationsData] = await Promise.all([
        apiService.getPatients(1, 1000),
        apiService.getUsers(1, 1000),
        apiService.getAllLocations(),
      ]);
      setPatients(patientsData.patients || []);
      setUsers(usersData.users || []);
      setLocations(locationsData || []);
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchDropdowns();
    fetchStatusCounts(); // Fetch status counts on component mount
  }, [fetchDropdowns, fetchStatusCounts]);

  // Combined fetch effect - handles both search and non-search cases
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const searchTerm = searchValue && searchValue.trim() !== '' ? searchValue : '';
      console.log('Fetch effect triggered for:', { searchTerm, page, rowsPerPage, selectedStatus });
      fetchOrders(page, rowsPerPage, searchTerm, selectedStatus);
    }, searchValue && searchValue.trim() !== '' ? 300 : 0); // Debounce only for search

    return () => clearTimeout(timeoutId);
  }, [searchValue, page, rowsPerPage, selectedStatus, fetchOrders]);

  // Separate effect to preserve focus after search completes
  useEffect(() => {
    if (searchInputRef && document.activeElement !== searchInputRef) {
      // Only preserve focus if the search input was previously focused
      const wasSearchFocused = searchInputRef === document.activeElement || 
                              searchInputRef.contains(document.activeElement);
      if (wasSearchFocused) {
        setTimeout(() => {
          if (searchInputRef && document.activeElement !== searchInputRef) {
            searchInputRef.focus();
            // Move cursor to end of text
            const length = searchInputRef.value.length;
            searchInputRef.setSelectionRange(length, length);
          }
        }, 100);
      }
    }
  }, [orders, searchInputRef]); // Only run when orders change (after search completes)

  // Refresh status counts when tab changes (to get updated counts)
  useEffect(() => {
    // Refresh counts when status changes to keep them current
    fetchStatusCounts();
  }, [selectedStatus, fetchStatusCounts]);

  // Status tabs configuration
  const statusTabs = [
    { label: 'In Progress', value: 0, status: 'In Progress' },
    { label: 'Completed', value: 1, status: 'Completed / Finalized' },
    { label: 'Held', value: 2, status: 'Held' },
    { label: 'Pending QA Review', value: 3, status: 'Pending QA Review' },
    { label: 'Others', value: 4, status: 'Others' },
  ];

  // Handle status tab change
  const handleStatusTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedStatusTab(newValue);
    const selectedTab = statusTabs[newValue];
    // Pass the actual status value to the server, including "Others"
    handleStatusChange(selectedTab.status);
  };

  const handleLocationToggle = (locationId: string) => {
    setSelectedLocationIds(prev => 
      prev.includes(locationId)
        ? prev.filter(id => id !== locationId)
        : [...prev, locationId]
    );
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return <CheckCircleIcon sx={{ fontSize: 16, color: '#4caf50' }} />;
      case 'pending':
        return <PendingIcon sx={{ fontSize: 16, color: '#ff9800' }} />;
      case 'error':
        return <ErrorIcon sx={{ fontSize: 16, color: '#f44336' }} />;
      default:
        return <PendingIcon sx={{ fontSize: 16, color: '#9e9e9e' }} />;
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  if (isLoading) {
    return <OrdersLoadingSkeleton />;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1a237e' }}>
            Orders
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage laboratory orders and accessions
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              fetchOrders(page, rowsPerPage, searchValue, selectedStatus);
              fetchStatusCounts();
              fetchDropdowns();
            }}
            disabled={loading || ordersLoading}
            sx={{
              borderColor: '#1976d2',
              color: '#1976d2',
              '&:hover': {
                borderColor: '#1565c0',
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
              },
              '&:disabled': {
                borderColor: '#ccc',
                color: '#ccc',
              }
            }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              handleEdit(null as any);
            }}
            sx={{
              background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
              boxShadow: '0 3px 5px 2px rgba(25, 118, 210, .3)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
              }
            }}
          >
            New Accessioning
          </Button>
        </Box>
      </Box>

      {/* Search Box */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: 2 }}>
                     <TextField
             fullWidth
             variant="outlined"
             placeholder="Search by patient name or accession number..."
             value={searchValue}
             onChange={e => setSearchValue(e.target.value)}
             inputRef={(input) => setSearchInputRef(input)}
             sx={{
               '& .MuiOutlinedInput-root': {
                 borderRadius: 2,
                 '&:hover fieldset': {
                   borderColor: '#1976d2',
                 },
                 '&.Mui-focused fieldset': {
                   borderColor: '#1976d2',
                 },
               },
             }}
             InputProps={{
               startAdornment: (
                 <InputAdornment position="start">
                   <SearchIcon sx={{ color: '#666' }} />
                 </InputAdornment>
               ),
               endAdornment: searchValue && (
                 <InputAdornment position="end">
                   <IconButton
                     size="small"
                     onClick={() => {
                       setSearchValue('');
                       // Focus back to search input after clearing
                       setTimeout(() => {
                         if (searchInputRef && document.activeElement !== searchInputRef) {
                           searchInputRef.focus();
                           const length = searchInputRef.value.length;
                           searchInputRef.setSelectionRange(length, length);
                         }
                       }, 100);
                     }}
                     edge="end"
                     sx={{ color: '#666' }}
                   >
                     <ClearIcon />
                   </IconButton>
                 </InputAdornment>
               ),
             }}
           />
        </CardContent>
      </Card>

      {/* Status Tabs */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: 0 }}>
          <Tabs
            value={selectedStatusTab}
            onChange={handleStatusTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                minWidth: 'auto',
                padding: '12px 16px',
                fontSize: '0.875rem',
                fontWeight: 500,
                textTransform: 'none',
                '&.Mui-selected': {
                  color: '#1976d2',
                  fontWeight: 600,
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#1976d2',
              },
            }}
          >
            {statusTabs.map((tab) => {
              const count = statusCounts[tab.status] || 0;
              const labelWithCount = `${tab.label} (${count})`;
              return (
                <Tab
                  key={tab.value}
                  label={labelWithCount}
                  value={tab.value}
                />
              );
            })}
          </Tabs>
        </CardContent>
      </Card>

      {/* Orders Grid */}
      <Card sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)', borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <AssignmentIcon sx={{ fontSize: 18 }} />
                    <span>Accession #</span>
                    <IconButton 
                      size="small" 
                      onClick={(e) => setAccessionFilterAnchorEl(e.currentTarget)} 
                      color={accessionFilterValue ? 'primary' : 'default'}
                      sx={{ ml: 1 }}
                    >
                      <FilterListIcon />
                    </IconButton>
                    <Menu
                      anchorEl={accessionFilterAnchorEl}
                      open={Boolean(accessionFilterAnchorEl)}
                      onClose={() => setAccessionFilterAnchorEl(null)}
                      MenuListProps={{ dense: true }}
                    >
                      <MenuItem disabled>Filter by Accession #</MenuItem>
                      <MenuItem>
                        <TextField
                          value={accessionFilterValue}
                          onChange={e => setAccessionFilterValue(e.target.value)}
                          placeholder="Enter accession #"
                          size="small"
                          autoFocus
                        />
                      </MenuItem>
                      <MenuItem onClick={clearAccessionFilter} disabled={!accessionFilterValue}>
                        Clear Filter
                      </MenuItem>
                    </Menu>
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PersonIcon sx={{ fontSize: 18 }} />
                    <span>Patient</span>
                    <IconButton 
                      size="small" 
                      onClick={(e) => setPatientFilterAnchorEl(e.currentTarget)} 
                      color={patientFilterValue ? 'primary' : 'default'}
                      sx={{ ml: 1 }}
                    >
                      <FilterListIcon />
                    </IconButton>
                    <Menu
                      anchorEl={patientFilterAnchorEl}
                      open={Boolean(patientFilterAnchorEl)}
                      onClose={() => setPatientFilterAnchorEl(null)}
                      MenuListProps={{ dense: true }}
                    >
                      <MenuItem disabled>Filter by Patient</MenuItem>
                      <MenuItem>
                        <TextField
                          value={patientFilterValue}
                          onChange={e => setPatientFilterValue(e.target.value)}
                          placeholder="Enter patient name or MRN"
                          size="small"
                          autoFocus
                        />
                      </MenuItem>
                      <MenuItem onClick={clearPatientFilter} disabled={!patientFilterValue}>
                        Clear Filter
                      </MenuItem>
                    </Menu>
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <ScheduleIcon sx={{ fontSize: 18 }} />
                    Collection Date/Time
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <ScienceIcon sx={{ fontSize: 18 }} />
                    Specimen Class
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <HospitalIcon sx={{ fontSize: 18 }} />
                    Ordering Physician
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <PathologistIcon sx={{ fontSize: 18 }} />
                    Assigned Pathologist
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <LocationIcon sx={{ fontSize: 18 }} />
                    <span>Locations</span>
                    <IconButton 
                      size="small" 
                      onClick={(e) => setLocationFilterAnchorEl(e.currentTarget)} 
                      color={selectedLocationIds.length > 0 ? 'primary' : 'default'}
                      sx={{ ml: 1 }}
                    >
                      <FilterListIcon />
                    </IconButton>
                    <Menu
                      anchorEl={locationFilterAnchorEl}
                      open={Boolean(locationFilterAnchorEl)}
                      onClose={() => setLocationFilterAnchorEl(null)}
                      MenuListProps={{ dense: true }}
                    >
                      <MenuItem disabled>Filter by Location</MenuItem>
                      {locations.map(loc => (
                        <MenuItem 
                          key={loc.location_id} 
                          onClick={() => handleLocationToggle(String(loc.location_id))}
                        >
                          <Checkbox checked={selectedLocationIds.includes(String(loc.location_id))} />
                          <ListItemText primary={loc.labName} />
                        </MenuItem>
                      ))}
                      <MenuItem onClick={clearLocationFilter} disabled={selectedLocationIds.length === 0}>
                        Clear Filter
                      </MenuItem>
                    </Menu>
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <TableSortLabel
                      active={sortColumn === 'status'}
                      direction={sortColumn === 'status' ? sortDirection : 'asc'}
                      onClick={() => handleSort('status')}
                    >
                      Status
                    </TableSortLabel>
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
                         <TableBody>
              {orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6" color="text.secondary">
                        No orders found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        No orders match the selected status filter
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                (orders || []).map((order) => {
                  // Handle potential type mismatches between string and number IDs
                  const patient = patients.find(p => {
                    const orderPatientId = order.patientId;
                    const patientId = p.id; // Use p.id (UUID) to match order.patientId (UUID)
                    return String(patientId) === String(orderPatientId);
                  });
                  const location = locations.find(l => {
                    const orderLocationId = order.accessionLocationId;
                    const locationId = l.location_id;
                    
                    // Handle both string and number IDs
                    if (orderLocationId && locationId) {
                      return String(locationId) === String(orderLocationId);
                    }
                    return false;
                  });
                  
                  
                  
                  const lastOrders = patientOrdersMap[order.patientId] || [];
                  
                  return (
                    <OrderTableRow
                      key={order.id}
                      order={order}
                      patient={patient}
                      location={location}
                      users={users}
                      lastOrders={lastOrders}
                      isPathologist={isPathologist}
                      onEdit={handleEdit}
                      onDelete={(id) => deleteOrder(id, searchValue)}
                      onGeneratePDF={generatePDF}
                      onAmend={handleAmend}
                      onAddendum={handleAddendum}
                      onArchive={handleArchive}
                      onViewVersionHistory={handleViewVersionHistory}
                      pdfGenerating={pdfGenerating}
                      pdfGeneratingOrderId={pdfGeneratingOrderId}
                    />
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <Box sx={{ borderTop: '1px solid #e0e0e0' }}>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontWeight: 500,
                color: '#666'
              }
            }}
          />
        </Box>
      </Card>

      {/* Dialogs */}
      <OrderDialog
        open={openDialog}
        onClose={handleDialogClose}
        editingOrder={editingOrder}
        isAddendumMode={isAddendumMode}
        isAmendMode={isAmendMode}
        onSave={handleSave}
        onFinalize={handleFinalize}
      />

      <VersionHistoryDialog
        open={versionHistoryOpen}
        onClose={() => setVersionHistoryOpen(false)}
        versionHistory={versionHistory}
        loading={versionHistoryLoading}
        onVersionSelect={(version) => {
          handleEdit(version);
        }}
      />

      {/* PDF Generation Snackbar */}
      <Snackbar
        open={pdfGenerating}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ zIndex: 9999 }}
      >
        <Alert
          severity="info"
          variant="filled"
          icon={<CircularProgress size={20} sx={{ color: 'white' }} />}
          sx={{
            backgroundColor: '#1976d2',
            color: 'white',
            '& .MuiAlert-icon': {
              color: 'white',
            },
          }}
        >
          Generating PDF report...
        </Alert>
      </Snackbar>

      {/* PDF Success Snackbar */}
      <Snackbar
        open={pdfSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ zIndex: 9999 }}
      >
        <Alert
          severity="success"
          variant="filled"
          sx={{
            backgroundColor: '#4caf50',
            color: 'white',
          }}
        >
          PDF report generated and downloaded successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
});

// Loading skeleton component
const OrdersLoadingSkeleton: React.FC = () => (
  <Box sx={{ p: 3 }}>
    {/* Header skeleton */}
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
      <Box>
        <Skeleton variant="text" width={120} height={40} />
        <Skeleton variant="text" width={200} height={20} />
      </Box>
      <Box display="flex" gap={2}>
        <Skeleton variant="rectangular" width={100} height={36} />
        <Skeleton variant="rectangular" width={120} height={36} />
      </Box>
    </Box>

    {/* Search and filters skeleton */}
    <Box display="flex" gap={2} mb={3} flexWrap="wrap">
      <Skeleton variant="rectangular" width={300} height={40} />
      <Skeleton variant="rectangular" width={150} height={40} />
      <Skeleton variant="rectangular" width={150} height={40} />
      <Skeleton variant="rectangular" width={150} height={40} />
    </Box>

    {/* Table skeleton */}
    <Card>
      <CardContent>
        <Skeleton variant="rectangular" width="100%" height={400} />
      </CardContent>
    </Card>
  </Box>
);

OrdersList.displayName = 'OrdersList';

export default OrdersList; 